'use client';

import { ButtonLink } from '~/components/primitives';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLocalStorageState } from '~/modules/browser/storage';
import { useI18n } from '~/features/i18n';
import { useMemo } from 'react';
import { cx } from 'class-variance-authority';
import { I18N_CONF } from '@funkey/locales/config';
import { usePortalCtx } from '~/features/global/portal';
import { HISTORY } from '~/modules/browser/history';
import { useState, useEffect } from 'react';
import { useThrottledCallback } from '~/components/hooks/throttling';
import { withoutDefaultParticipants } from '~/features/activities/search/shared';

const SCROLL_SMOOTH_DISABLED_CLASS = 'disable-scroll-smooth';

/**
 * Carries the group size the visitor is looking at back to the page they came
 * from.
 *
 * Every route back is a URL captured before this page opened: `returnUrl` at the
 * moment the card was clicked, the stored pages when they were left. Change the
 * participants here and that URL still asks for the old size, so going back
 * reprices the whole results page to a number the visitor has already moved on
 * from. The results page leaves the default out of its URL, so the default
 * removes the parameter rather than writing it.
 */
function withCurrentParticipants(href: string, participants: number | null): string {
    // Nothing to carry: the visitor never set a size here, so whatever the page
    // they came from asked for is still what they asked for.
    if (participants === null) return href;

    // The stored routes back append the hash to the query, so it has to come off
    // before the rest is parsed as parameters — left on, it reads as part of the
    // last value and is gone by the time the parameters are written back out.
    const [beforeHash = href, ...hashParts] = href.split('#');
    const hash = hashParts.length > 0 ? `#${hashParts.join('#')}` : '';
    const [path = beforeHash, ...queryParts] = beforeHash.split('?');
    const params = new URLSearchParams(queryParts.join('?'));

    const carried = withoutDefaultParticipants(participants);
    if (carried === null) params.delete('participants');
    else params.set('participants', String(carried));

    return params.size > 0 ? `${path}?${params}${hash}` : `${path}${hash}`;
}

export const SmartBackNavigation: React.FC = () => {
    const portal = usePortalCtx();
    const i18n = useI18n();

    const pathname = usePathname();
    const searchParams = useSearchParams();
    const visitedSpecialPages = useLocalStorageState(HISTORY.specialPages);
    const previousPage = useLocalStorageState(HISTORY.previous);

    const isScrolled = useResponsiveScrollThreshold();

    // Disable scroll-smooth
    useEffect(() => {
        const body = document.body;
        body.classList.add(SCROLL_SMOOTH_DISABLED_CLASS);
        return () => {
            body.classList.remove(SCROLL_SMOOTH_DISABLED_CLASS);
        };
    }, []);

    const returnUrl = searchParams.get('returnUrl');
    const participants = Number(searchParams.get('participants')) || null;

    const navigationInfo = useMemo(() => {
        const backToOverView = visitedSpecialPages.state['PDP.backToOverview'];

        // Prioritize returnUrl (used when opening in new tab via window.open)
        // window.open duplicates sessionStorage, so previousPage/backToOverview
        // may contain stale values from the parent tab
        if (returnUrl) {
            return {
                type: 'link' as const,
                href: withCurrentParticipants(returnUrl, participants),
            };
        }

        // Check if we have a valid backToOverview
        if (backToOverView?.pathname && backToOverView.portal === portal.portal) {
            const fullUrl = `${backToOverView.pathname}${backToOverView.search ?? ''}${backToOverView.hash ?? ''}`;
            return {
                type: 'link' as const,
                href: withCurrentParticipants(fullUrl, participants),
            };
        }

        // If we have a previous page in history, use it as a concrete link
        // But skip it if it points to the current page or another PDP page
        if (previousPage.state) {
            const prev = previousPage.state;
            const activityPath = I18N_CONF.getPageSlugForLocale('activities', i18n.locale);
            const isCurrentPage = prev.pathname === pathname;
            const isPDPPage = prev.pathname.includes(`/${activityPath}/`);

            if (!isCurrentPage && !isPDPPage) {
                const fullUrl = `${prev.pathname}${prev.search ?? ''}${prev.hash ?? ''}`;
                return {
                    type: 'link' as const,
                    href: withCurrentParticipants(fullUrl, participants),
                };
            }
        }

        // Fallback: no returnUrl, no backToOverview, no previous page
        const fallbackUrl =
            portal.portal === 'BIZZ'
                ? `${portal.baseURLPath}/booking`
                : I18N_CONF.getTranslatedUrl('teambuildings', { locale: i18n.locale });

        return {
            type: 'link' as const,
            href: fallbackUrl,
        };
    }, [
        portal.portal,
        visitedSpecialPages.state,
        previousPage.state,
        i18n.locale,
        returnUrl,
        participants,
        pathname,
    ]);

    const backText = i18n._('navigation.back.button');

    const commonButtonProps = {
        variant: 'ghost' as const,
        iconBefore: 'arrow-left' as const,
        size: 'md' as const,
    };

    return (
        <>
            <ButtonLink
                {...commonButtonProps}
                href={navigationInfo.href}
                scroll={false}
                className="mb-12 -translate-x-4 rounded-full bg-white/0 lg:absolute lg:mb-0 lg:-translate-y-20"
            >
                {backText}
            </ButtonLink>

            {isScrolled && (
                // Desktop only, where it sits in the left margin. On mobile there is no margin
                // to sit in, so it floated over the content it was pinned above — and, as a
                // full-width block, swallowed every click along its line. `w-fit` +
                // `pointer-events-none` keep it harmless on the sizes that do show it.
                <div
                    aria-hidden
                    className="pointer-events-none sticky top-30 z-2 hidden w-fit lg:block"
                >
                    <ButtonLink
                        {...commonButtonProps}
                        href={navigationInfo.href}
                        scroll={false}
                        title={backText}
                        className={cx(
                            // Translucent + blurred rather than solid white: it floats over
                            // whatever is scrolling past, and against pale content a white pill
                            // on white simply vanished. The blur and ring keep its edge readable
                            // whatever is behind it.
                            'pointer-events-auto rounded-full bg-white/70 shadow-sm ring-1 ring-black/5 backdrop-blur-md',
                            portal.portal === 'BIZZ' ? '2xl:-translate-x-18' : 'xl:-translate-x-18',
                        )}
                    />
                </div>
            )}
        </>
    );
};

const MOBILE_BREAKPOINT = 640 as const;
const TABLET_BREAKPOINT = 1024 as const;
const MOBILE_THRESHOLD = 64 as const;
const TABLET_THRESHOLD = 250 as const;
const DESKTOP_THRESHOLD = 400 as const;

export function useResponsiveScrollThreshold() {
    const [isPastThreshold, setIsPastThreshold] = useState<boolean>(false);

    const syncState = useThrottledCallback(() => {
        function getThreshold() {
            const width = window.innerWidth;
            if (width < MOBILE_BREAKPOINT) return MOBILE_THRESHOLD;
            if (width < TABLET_BREAKPOINT) return TABLET_THRESHOLD;
            return DESKTOP_THRESHOLD;
        }

        return setIsPastThreshold(window.scrollY > getThreshold());
    }, 100);

    useEffect(() => {
        const abort = new AbortController();

        syncState();
        window.addEventListener('resize', syncState, { signal: abort.signal });
        window.addEventListener('scroll', syncState, { signal: abort.signal });
        return () => abort.abort();
    }, []);

    return isPastThreshold;
}
